import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { filter } from 'rxjs';

import { ListItem, ListProperty } from '@site-mate/dashpivot-shared-library';

import { IListPropertyCell } from '../list-cell.interface';
import { ListPropertyCellService } from '../list-property-cell.service';

@Component({
  templateUrl: './list-field-number.component.html',
  styleUrls: ['./list-field-number.component.scss'],
  selector: 'cc-list-field-number',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
})
export class ListFieldNumberComponent implements IListPropertyCell {
  @Input() item: ListItem;
  @Input() property: ListProperty;
  @Output() readonly loadPlaceholder = new EventEmitter<void>();
  @Output() readonly propertyUpdated = new EventEmitter<Record<string, unknown>>();
  @ViewChild('textInput') textInput: ElementRef<HTMLTextAreaElement>;
  inputValue = '';
  private destroyRef = inject(DestroyRef);
  constructor(private readonly listPropertyCellService: ListPropertyCellService) {}

  ngOnInit() {
    this.inputValue = String(this.property.value ?? '');
    this.monitorExternalClicks();
  }

  private monitorExternalClicks() {
    const cellId = `${this.property._id}/${this.item._id}`;
    this.listPropertyCellService.setActiveCell(cellId);
    this.listPropertyCellService.$activeCell
      .pipe(
        filter((id) => id !== cellId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.loadPlaceholder.emit());
  }

  ngAfterViewInit() {
    this.textInput.nativeElement.focus();
  }

  isNumber(value: string) {
    return !Number.isNaN(Number(value));
  }

  onPropertyUpdated(value: string) {
    const isEmptyString = value === '';
    const isNumber = value && this.isNumber(value);
    const hasChanged = value !== this.property.value;

    if (isEmptyString || (isNumber && hasChanged)) {
      this.property.value = value;
      this.propertyUpdated.emit({ value });
    }
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this.loadPlaceholder.emit();
  }
}
